import { useEffect, useState } from "react";
import axios from "axios";
import { PaymentForm, CreditCard } from "react-square-web-payments-sdk";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { useToast } from "../hooks/use-toast";
import { Lock, Shield, CreditCard as CreditCardIcon } from "lucide-react";
import { useDiscountFromLink } from "../hooks/useDiscountFromLink";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Paid signup card capture modal — collects a card via Square's Web
 * Payments SDK, saves it on file, CHARGES the first month immediately,
 * then creates a Square Subscription with `start_date = today + 30 days`.
 *
 * This is the correct paid-signup pattern. Replaces the old Payment-Link
 * checkout flow which silently failed to create recurring Subscriptions
 * (root cause documented in backend `create-subscription-with-card`).
 *
 * Props:
 *  - open: boolean
 *  - onOpenChange: (open: boolean) => void
 *  - tier: "solo" | "pro" | "crew"
 *  - authToken: string (Bearer token)
 *  - tierPrice: number (£ per month, full price for display)
 *  - couponCode?: string
 *  - couponDiscountPct?: number (for display strikethrough)
 *  - onSuccess: (response) => void
 */
export default function PaidCardCapture({
  open,
  onOpenChange,
  tier,
  authToken,
  tierPrice,
  couponCode,
  couponDiscountPct,
  onSuccess,
}) {
  const { toast } = useToast();
  const discountLink = useDiscountFromLink();
  const [config, setConfig] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingConfig(true);
    axios
      .get(`${API_URL}/api/payments/square-config`)
      .then((res) => {
        if (!cancelled) setConfig(res.data);
      })
      .catch((err) => {
        if (!cancelled) {
          toast({
            title: "Couldn't load card form",
            description:
              err.response?.data?.detail ||
              "Square Web Payments isn't configured. Please contact support.",
            variant: "destructive",
          });
          onOpenChange(false);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingConfig(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const computeDisplayPrice = () => {
    if (!tierPrice) return null;
    let price = tierPrice;
    let was = null;
    let pct = null;

    if (discountLink) {
      const pence = Math.round(tierPrice * 100);
      const discountedPence = Math.floor(pence * (1 - discountLink.pct / 100));
      price = discountedPence / 100;
      was = tierPrice;
      pct = discountLink.pct;
    } else if (couponDiscountPct) {
      price = tierPrice * (1 - couponDiscountPct / 100);
      was = tierPrice;
      pct = couponDiscountPct;
    }
    return {
      now: price.toFixed(2),
      was: was != null ? was.toFixed(2) : null,
      pct,
    };
  };
  const price = computeDisplayPrice();

  const nextBillingDate = new Date();
  nextBillingDate.setDate(nextBillingDate.getDate() + 30);
  const nextBillingDisplay = nextBillingDate.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const handleCardTokenized = async (tokenResult) => {
    if (tokenResult.status !== "OK") {
      toast({
        title: "Card validation failed",
        description: tokenResult.errors?.[0]?.detail || "Please check your card details.",
        variant: "destructive",
      });
      return;
    }
    setSubmitting(true);
    try {
      const body = { source_id: tokenResult.token, tier };
      if (couponCode) body.coupon_code = couponCode;
      const res = await axios.post(
        `${API_URL}/api/payments/create-subscription-with-card`,
        body,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      toast({
        title: "Subscription active!",
        description: res.data.message,
      });
      localStorage.removeItem("discount_link_slug");
      localStorage.removeItem("discount_link_name");
      localStorage.removeItem("discount_link_pct");
      onSuccess?.(res.data);
    } catch (err) {
      toast({
        title: "Couldn't complete subscription",
        description: err.response?.data?.detail || "Please try again or contact support.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const tierLabel = (tier || "").charAt(0).toUpperCase() + (tier || "").slice(1);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="paid-card-capture-modal">
        <DialogHeader>
          <DialogTitle className="text-xl">Subscribe to {tierLabel}</DialogTitle>
          <DialogDescription>
            Enter your card to start your subscription.{" "}
            <span className="font-medium text-emerald-700">
              You&apos;ll be charged £{price?.now} today.
            </span>
          </DialogDescription>
        </DialogHeader>

        {/* Plan summary */}
        <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-4 space-y-2 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-zinc-600">Plan</span>
            <span className="font-semibold capitalize">{tier}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-zinc-600">First charge</span>
            <span className="font-semibold" data-testid="paid-first-charge">
              £{price?.now} today
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-zinc-600">Next charge</span>
            <span className="font-semibold">{nextBillingDisplay}</span>
          </div>
          <div className="flex items-center justify-between pt-1 border-t border-zinc-200">
            <span className="text-zinc-600">Then</span>
            <span className="font-bold text-zinc-900" data-testid="paid-recurring-price">
              {price?.was ? (
                <>
                  £{price.now}
                  <span className="text-zinc-400 line-through ml-1.5 font-normal text-xs">£{price.was}</span>
                  <span className="ml-1.5 text-xs text-emerald-600 font-medium">{price.pct}% off</span>
                </>
              ) : (
                <>£{price?.now}</>
              )}
              <span className="text-zinc-500 font-normal text-xs">/mo</span>
            </span>
          </div>
        </div>

        {/* Card form */}
        <div className="mt-2">
          {loadingConfig && (
            <div className="h-32 flex items-center justify-center text-sm text-zinc-500">
              Loading secure card form…
            </div>
          )}
          {!loadingConfig && config && (
            <PaymentForm
              applicationId={config.application_id}
              locationId={config.location_id}
              cardTokenizeResponseReceived={handleCardTokenized}
            >
              <CreditCard
                buttonProps={{
                  isLoading: submitting,
                  css: {
                    backgroundColor: "#18181b",
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#fff",
                    "&:hover": { backgroundColor: "#27272a" },
                  },
                }}
              >
                {submitting ? "Processing…" : `Pay £${price?.now} & subscribe`}
              </CreditCard>
            </PaymentForm>
          )}
        </div>

        {/* Trust row */}
        <div className="flex items-center justify-center gap-4 pt-2 text-xs text-zinc-500">
          <span className="inline-flex items-center gap-1">
            <Lock className="h-3 w-3" /> Encrypted
          </span>
          <span className="inline-flex items-center gap-1">
            <Shield className="h-3 w-3" /> PCI-DSS
          </span>
          <span className="inline-flex items-center gap-1">
            <CreditCardIcon className="h-3 w-3" /> Cancel any time
          </span>
        </div>

        <p className="text-xs text-zinc-500 leading-relaxed text-center">
          By subscribing you authorise QuoteSorted to charge your card{" "}
          <span className="font-semibold">£{price?.now}</span> today, and the same amount
          every month thereafter starting {nextBillingDisplay}. Cancel any time.
        </p>
      </DialogContent>
    </Dialog>
  );
}
